import React, { useState, useEffect } from 'react'

import useDownloadPDF from 'hooks/useDownloadPdf'
import { Control, Controller, FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import remarkGfm from 'remark-gfm'
import Markdown from 'react-markdown'
import { UserTask, UserTaskHowToStep } from '__generated__/api-types-and-hooks'
import AppTutorialError from 'appAssets/AppTutorialError.png'
import { AppButton } from 'components/Common/AppButton/AppButton.component'
import { AppVideoPlayer } from 'components/Common/AppVideoPlayer/AppVideoPlayer.component'
import { AppGenerateTutorialLoader } from 'components/Common/AppSvgIcons/AppGenerateTutorialLoader'
import { AppCombinedVideoCard } from './AppCombinedVideoCard.component'
import { JumpToVideoCard } from './JumpToVideoCard.component'
import 'components/AppOnboarding/AppChatbot/AppChatBotFlows/styles.css'

// Maximum number of videos to show per step
const MAX_VIDEOS_PER_STEP = 2

interface IAppGenerateTutorial {
  task: UserTask
  planName: string
  isLoading: boolean
  isViewMode: boolean
  generatedHowToDo: string
  tutorialNotFound: boolean
  control: Control<FieldValues>
  structuredSteps?: UserTaskHowToStep[]
}

export const AppGenerateTutorial: React.FC<IAppGenerateTutorial> = ({
  task,
  control,
  planName,
  isLoading,
  isViewMode,
  generatedHowToDo,
  tutorialNotFound,
  structuredSteps,
}) => {
  const [activeTab, setActiveTab] = useState<'text' | 'video'>('text')
  const downloadPdf = useDownloadPDF()
  const { t } = useTranslation()

  // Reset tab to text when new tutorial is generated
  useEffect(() => {
    if (!isLoading && (generatedHowToDo || task?.howToDo)) {
      setActiveTab('text')
    }
  }, [isLoading, generatedHowToDo, task?.howToDo])

  const cleanText = (text) => {
    return text.replace(/\*\*/g, '').trim()
  }

  // Feedback component - matching Figma design
  const VideoFeedback = ({ videoTitle, videoUrl }: { videoTitle: string; videoUrl: string }) => {
    const [feedback, setFeedback] = useState<'helpful' | 'not-helpful' | null>(null)

    const handleFeedback = (type: 'helpful' | 'not-helpful') => {
      setFeedback(type)
      console.log(`Video "${videoTitle}" marked as ${type}`)
    }

    return (
      <div className="flex items-center gap-3 mt-6 mb-6 bg-white rounded-lg">
        {/* Coach Logic Avatar */}
        <div className="flex-shrink-0">
          <img
            src="/images/coach-logic-avatar.png"
            alt="Coach Logic"
            className="w-8 h-8 rounded-full object-cover"
          />
        </div>

        {/* Name */}
        <span className="text-sm font-medium text-app-grey-90 font-inter flex-shrink-0">
          {t('playboard.playForm.coachLogic')}
        </span>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Feedback Question and Buttons */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-app-grey-70 font-inter">
            {t('playboard.playForm.wasItHelpful')}
          </span>
          <div className="flex items-center">
            <button
              onClick={() => handleFeedback('helpful')}
              className={`flex items-center gap-3 px-3 py-1.5 rounded-md text-sm font-inter transition-all ${
                feedback === 'helpful' ? 'bg-green-500 text-white' : 'hover:text-app-grey-90'
              }`}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 15 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9.33073 3.25329L8.66406 5.99996H12.5507C12.7577 5.99996 12.9619 6.04815 13.147 6.14072C13.3322 6.23329 13.4932 6.3677 13.6174 6.53329C13.7416 6.69889 13.8255 6.89112 13.8626 7.09478C13.8996 7.29843 13.8887 7.50791 13.8307 7.70663L12.2774 13.04C12.1966 13.3169 12.0282 13.5602 11.7974 13.7333C11.5666 13.9064 11.2859 14 10.9974 14H1.9974C1.64377 14 1.30464 13.8595 1.05459 13.6094C0.804538 13.3594 0.664063 13.0202 0.664062 12.6666V7.33329C0.664062 6.97967 0.804538 6.64053 1.05459 6.39048C1.30464 6.14044 1.64377 5.99996 1.9974 5.99996H3.8374C4.08545 5.99983 4.32855 5.9305 4.53937 5.79977C4.75018 5.66905 4.92034 5.4821 5.03073 5.25996L7.33073 0.666626C7.64511 0.670519 7.95456 0.745405 8.23593 0.88569C8.51731 1.02597 8.76335 1.22803 8.95567 1.47676C9.14798 1.72549 9.28161 2.01447 9.34655 2.32209C9.4115 2.62972 9.40609 2.94805 9.33073 3.25329Z"
                  stroke="#0A0A0A"
                  strokeWidth="1.33333"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {t('playboard.playForm.feedbackYes')}
            </button>
            <button
              onClick={() => handleFeedback('not-helpful')}
              className={`flex items-center gap-3 px-3 py-1.5 rounded-md text-sm font-inter transition-all ${
                feedback === 'not-helpful' ? 'bg-red-500 text-white' : 'hover:text-app-grey-90'
              }`}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 15 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5.2174 11.4133L5.88406 8.66663H1.9974C1.7904 8.66663 1.58625 8.61843 1.40111 8.52586C1.21597 8.43329 1.05493 8.29889 0.930729 8.13329C0.806533 7.9677 0.722597 7.77546 0.685569 7.57181C0.648541 7.36815 0.659438 7.15867 0.717396 6.95996L2.27073 1.62663C2.35151 1.34967 2.51993 1.10639 2.75073 0.933293C2.98152 0.760197 3.26224 0.666626 3.55073 0.666626H12.5507C12.9044 0.666626 13.2435 0.807102 13.4935 1.05715C13.7436 1.3072 13.8841 1.64634 13.8841 1.99996V7.33329C13.8841 7.68691 13.7436 8.02605 13.4935 8.2761C13.2435 8.52615 12.9044 8.66663 12.5507 8.66663H10.7107C10.4627 8.66676 10.2196 8.73609 10.0088 8.86681C9.79795 8.99754 9.62778 9.18448 9.5174 9.40663L7.2174 14C6.90301 13.9961 6.59357 13.9212 6.31219 13.7809C6.03082 13.6406 5.78478 13.4386 5.59246 13.1898C5.40014 12.9411 5.26652 12.6521 5.20157 12.3445C5.13662 12.0369 5.14203 11.7185 5.2174 11.4133Z"
                  stroke="#0A0A0A"
                  strokeWidth="1.33333"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {t('playboard.playForm.feedbackNo')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // No video available component - matching Figma design
  const NoVideoAvailable = ({ stepTitle }: { stepTitle: string }) => {
    return (
      <div className="bg-[#EFF6FF] border border-[#BEDBFF] rounded-lg p-4 flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_19050_66881)">
              <path
                d="M9.9974 18.3333C14.5998 18.3333 18.3307 14.6023 18.3307 9.99996C18.3307 5.39759 14.5998 1.66663 9.9974 1.66663C5.39502 1.66663 1.66406 5.39759 1.66406 9.99996C1.66406 14.6023 5.39502 18.3333 9.9974 18.3333Z"
                stroke="#155DFC"
                strokeWidth="1.66667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10 6.66663V9.99996"
                stroke="#155DFC"
                strokeWidth="1.66667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10 13.3334H10.0083"
                stroke="#155DFC"
                strokeWidth="1.66667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
            <defs>
              <clipPath id="clip0_19050_66881">
                <rect width="20" height="20" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </div>
        <div className="flex-1 space-y-2">
          <p className="text-sm font-medium text-[#1C398E]">
            {t('playboard.playForm.videoNotAvailable')}
          </p>
          <p className="text-sm text-[#193CB8]">{t('playboard.playForm.referToTextTutorial')}</p>
          <button
            onClick={() => setActiveTab('text')}
            className="flex items-center gap-2 text-sm font-medium text-[#1447E6] underline hover:text-[#1C398E] transition-colors"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 4.66663V14"
                stroke="#1447E6"
                strokeWidth="1.33333"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2.0026 12C1.82579 12 1.65622 11.9298 1.5312 11.8047C1.40618 11.6797 1.33594 11.5101 1.33594 11.3333V2.66667C1.33594 2.48986 1.40618 2.32029 1.5312 2.19526C1.65622 2.07024 1.82579 2 2.0026 2H5.33594C6.04318 2 6.72146 2.28095 7.22156 2.78105C7.72165 3.28115 8.0026 3.95942 8.0026 4.66667C8.0026 3.95942 8.28356 3.28115 8.78365 2.78105C9.28375 2.28095 9.96203 2 10.6693 2H14.0026C14.1794 2 14.349 2.07024 14.474 2.19526C14.599 2.32029 14.6693 2.48986 14.6693 2.66667V11.3333C14.6693 11.5101 14.599 11.6797 14.474 11.8047C14.349 11.9298 14.1794 12 14.0026 12H10.0026C9.47217 12 8.96346 12.2107 8.58839 12.5858C8.21332 12.9609 8.0026 13.4696 8.0026 14C8.0026 13.4696 7.79189 12.9609 7.41682 12.5858C7.04174 12.2107 6.53304 12 6.0026 12H2.0026Z"
                stroke="#1447E6"
                strokeWidth="1.33333"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {t('playboard.playForm.viewTextTutorialFor', { step: stepTitle.toLowerCase() })}
          </button>
        </div>
      </div>
    )
  }

  // No video tutorials available - full screen empty state
  const NoVideoTutorialsEmptyState = () => {
    return (
      <div
        className="text-center py-12 rounded-xl border border-[#E2E8F0]"
        style={{ background: 'linear-gradient(to bottom right, #F8FAFC, #F1F5F9)' }}
      >
        <div className="mx-auto px-4">
          <div className="w-88 h-88 mx-auto mb-6 rounded-full flex items-center justify-center">
            <svg
              width="112"
              height="112"
              viewBox="0 0 112 112"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g filter="url(#filter0_dd_19147_103251)">
                <path
                  d="M12 46C12 21.6995 31.6995 2 56 2C80.3005 2 100 21.6995 100 46C100 70.3005 80.3005 90 56 90C31.6995 90 12 70.3005 12 46Z"
                  fill="white"
                  shape-rendering="crispEdges"
                />
                <path
                  d="M53.3203 34H60.0003C61.0612 34 62.0786 34.4214 62.8287 35.1716C63.5789 35.9217 64.0003 36.9391 64.0003 38V43L74.4963 36.876C74.6483 36.7873 74.821 36.7403 74.9969 36.7397C75.1729 36.7391 75.3459 36.785 75.4984 36.8726C75.651 36.9602 75.7778 37.0866 75.8659 37.2389C75.954 37.3912 76.0004 37.564 76.0003 37.74V54.132"
                  stroke="#90A1B9"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M64 54C64 55.0609 63.5786 56.0783 62.8284 56.8284C62.0783 57.5786 61.0609 58 60 58H40C38.9391 58 37.9217 57.5786 37.1716 56.8284C36.4214 56.0783 36 55.0609 36 54V38C36 36.9391 36.4214 35.9217 37.1716 35.1716C37.9217 34.4214 38.9391 34 40 34H44"
                  stroke="#90A1B9"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M36 26L76 66"
                  stroke="#90A1B9"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
              <defs>
                <filter
                  id="filter0_dd_19147_103251"
                  x="0"
                  y="0"
                  width="112"
                  height="112"
                  filterUnits="userSpaceOnUse"
                  color-interpolation-filters="sRGB"
                >
                  <feFlood flood-opacity="0" result="BackgroundImageFix" />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feMorphology
                    radius="4"
                    operator="erode"
                    in="SourceAlpha"
                    result="effect1_dropShadow_19147_103251"
                  />
                  <feOffset dy="4" />
                  <feGaussianBlur stdDeviation="3" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
                  <feBlend
                    mode="normal"
                    in2="BackgroundImageFix"
                    result="effect1_dropShadow_19147_103251"
                  />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feMorphology
                    radius="3"
                    operator="erode"
                    in="SourceAlpha"
                    result="effect2_dropShadow_19147_103251"
                  />
                  <feOffset dy="10" />
                  <feGaussianBlur stdDeviation="7.5" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
                  <feBlend
                    mode="normal"
                    in2="effect1_dropShadow_19147_103251"
                    result="effect2_dropShadow_19147_103251"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="effect2_dropShadow_19147_103251"
                    result="shape"
                  />
                </filter>
              </defs>
            </svg>
          </div>
          <h3 className="text-xl font-inter font-semibold text-app-black-100 mb-2">
            {t('playboard.playForm.noVideoTutorialsAvailable')}
          </h3>
          <p className="text-base text-app-grey-60 font-inter mb-6 leading-relaxed mx-auto">
            {t('playboard.playForm.noVideoTutorialsBody')}
          </p>
          <div className="flex justify-center">
            <AppButton
              label={t('playboard.playForm.viewTextTutorial')}
              variant="primary"
              size="md"
              onClick={() => setActiveTab('text')}
            />
          </div>
        </div>
      </div>
    )
  }

  const renderVideoStep = (step: UserTaskHowToStep, allSteps: UserTaskHowToStep[]) => {
    const videos = step.videos || []

    // Check if this step has a multi-step video (video that covers multiple steps)
    const multiStepVideos = videos.filter(
      (video) => video && video.stepsCovered && video.stepsCovered.length > 1
    )
    const allSingleStepVideos = videos.filter(
      (video) => video && (!video.stepsCovered || video.stepsCovered.length === 1)
    )

    // Check if we should show combined video card (only on first step of coverage)
    const shouldShowCombinedCard = multiStepVideos.some((video) => {
      if (!video) return false
      const covered = (video.stepsCovered || [])
        .filter((s): s is number => s !== null && s !== undefined)
        .sort((a, b) => a - b)
      return covered[0] === step.stepNumber // Show only on first step
    })

    // Find multi-step videos from previous steps that cover this step (for "Jump to video" card)
    const previousMultiStepVideos: Array<{
      video: NonNullable<typeof videos[0]>
      firstStepNumber: number
    }> = []

    allSteps.forEach((prevStep) => {
      if (prevStep.stepNumber >= step.stepNumber) return
      ;(prevStep.videos || []).forEach((video) => {
        if (!video) return
        const covered = (video.stepsCovered || [])
          .filter((s): s is number => s !== null && s !== undefined)
          .sort((a, b) => a - b)
        if (
          covered.length > 1 &&
          covered.includes(step.stepNumber) &&
          covered[0] !== step.stepNumber
        ) {
          previousMultiStepVideos.push({
            video,
            firstStepNumber: covered[0] || prevStep.stepNumber,
          })
        }
      })
    })

    // Helper function to scroll to a specific step
    const scrollToStep = (stepNumber: number) => {
      const stepElement = document.getElementById(`video-step-${stepNumber}`)
      if (stepElement) {
        stepElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }

    // Get titles of videos shown in "Jump to video" cards to avoid duplicates
    const jumpToVideoTitles = new Set(
      previousMultiStepVideos.map((item) => (item.video.title || '').toLowerCase().trim())
    )

    // Combined videos that start on this step (filter out duplicates from Jump to Video)
    const combinedVideosForThisStep = multiStepVideos
      .filter((video) => {
        if (!video) return false
        const covered = (video.stepsCovered || [])
          .filter((s): s is number => s !== null && s !== undefined)
          .sort((a, b) => a - b)
        return covered[0] === step.stepNumber
      })
      .filter((video) => !jumpToVideoTitles.has((video?.title || '').toLowerCase().trim()))

    // Filter single-step videos to exclude any that match "Jump to Video" titles
    const filteredSingleStepVideos = allSingleStepVideos.filter(
      (video) => !jumpToVideoTitles.has((video?.title || '').toLowerCase().trim())
    )

    // Limit TOTAL videos to MAX_VIDEOS_PER_STEP (combined + single)
    // If there's a "Jump to Video" card, count it as 1 video towards the limit
    // Priority: combined videos first, then single-step videos fill remaining slots
    const hasJumpToVideo = previousMultiStepVideos.length > 0
    const effectiveMaxVideos = hasJumpToVideo ? MAX_VIDEOS_PER_STEP - 1 : MAX_VIDEOS_PER_STEP
    const combinedCount = Math.min(combinedVideosForThisStep.length, effectiveMaxVideos)
    const combinedVideosToShow = combinedVideosForThisStep.slice(0, combinedCount)
    const remainingSlots = Math.max(0, effectiveMaxVideos - combinedCount)
    const singleStepVideos = filteredSingleStepVideos.slice(0, remainingSlots)

    return (
      <div
        key={step.stepNumber}
        id={`video-step-${step.stepNumber}`}
        className="mb-3 pb-3 border-b border-app-grey-40 last:border-b-0"
      >
        {/* Step header with number badge */}
        <div className="mb-2">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-8 h-8 bg-black text-white rounded-full flex items-center justify-center text-sm font-bold">
              {step.stepNumber}
            </div>
            <div className="flex-1">
              {/* Step Title */}
              <h3 className="text-base font-inter font-semibold text-app-black-100 mb-2">
                {step.title || t('playboard.playForm.step', { number: step.stepNumber })}
              </h3>
            </div>
          </div>
        </div>

        {/* Step description - brief summary */}
        <div className="mb-6 ml-11">
          <Markdown
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({ node, ...props }) => (
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                <a
                  {...props}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-app-blue-600 underline hover:text-app-blue-800 transition-colors"
                />
              ),
              p: ({ node, ...props }) => (
                <p
                  {...props}
                  className="mb-3 leading-relaxed text-app-grey-80 text-sm font-inter"
                />
              ),
              strong: ({ node, ...props }) => (
                <strong {...props} className="font-semibold text-app-black-100" />
              ),
              ul: ({ node, ...props }) => (
                <ul {...props} className="list-disc ml-4 pl-2 space-y-1 mt-2 mb-3" />
              ),
              li: ({ node, ...props }) => <li {...props} className="text-app-grey-80" />,
            }}
          >
            {step.description}
          </Markdown>
        </div>

        {/* Jump to Video Card - Show for steps covered by a multi-step video from a previous step */}
        {previousMultiStepVideos.length > 0 && (
          <div className="ml-11 mb-6">
            {previousMultiStepVideos.slice(0, 1).map((item, index) => {
              const stepsCovered = (item.video.stepsCovered || []).filter(
                (s): s is number => s !== null && s !== undefined
              )
              return (
                <JumpToVideoCard
                  key={index}
                  videoTitle={item.video.title || ''}
                  stepsCovered={stepsCovered}
                  onJumpToVideo={() => scrollToStep(item.firstStepNumber)}
                />
              )
            })}
          </div>
        )}

        {/* Video Options Section */}
        {((shouldShowCombinedCard && combinedVideosToShow.length > 0) ||
          singleStepVideos.length > 0) && (
          <div className="ml-11">
            <h3 className="text-sm font-normal text-app-grey-50 font-inter mb-6">
              {t('playboard.playForm.videoOptions')}
            </h3>
            <div className="space-y-6">
              {/* Combined Videos - Show only on first step of multi-step video */}
              {shouldShowCombinedCard &&
                combinedVideosToShow.map((video, index) => {
                  if (!video) return null
                  const stepsCovered = (video.stepsCovered || [step.stepNumber]).filter(
                    (s): s is number => s !== null && s !== undefined
                  )
                  return (
                    <div key={`combined-${index}`}>
                      <AppCombinedVideoCard
                        videoTitle={video.title || ''}
                        videoUrl={video.url || ''}
                        stepsCovered={stepsCovered}
                      />
                      {/* Feedback for combined video */}
                      <div className="mt-4">
                        <VideoFeedback videoTitle={video.title || ''} videoUrl={video.url || ''} />
                      </div>
                      {/* Separator if there are more videos */}
                      {(index < combinedVideosToShow.length - 1 || singleStepVideos.length > 0) && (
                        <div className="my-8 border-t border-app-grey-40" />
                      )}
                    </div>
                  )
                })}

              {/* Single-step videos */}
              {singleStepVideos.map((video, index) => {
                if (!video) return null
                const rawStepsCovered = video.stepsCovered || [step.stepNumber]
                const stepsCovered = rawStepsCovered.filter(
                  (s): s is number => s !== null && s !== undefined
                )
                const stepsLabel =
                  stepsCovered.length > 1
                    ? t('playboard.playForm.stepBadgeRange', {
                        from: stepsCovered[0],
                        to: stepsCovered[stepsCovered.length - 1],
                      })
                    : null

                return (
                  <div key={`single-${index}`}>
                    <div className="space-y-4">
                      {/* Video title with optional badge */}
                      <div className="flex items-center gap-2">
                        <h2 className="text-base font-semibold text-app-black-100 font-inter flex-1">
                          {video.title || ''}
                        </h2>
                        {stepsLabel && (
                          <div className="flex-shrink-0 bg-[#705AF8] text-white px-3 py-1 rounded-lg text-xs font-bold font-inter">
                            {stepsLabel}
                          </div>
                        )}
                      </div>

                      {/* Video player */}
                      <div className="rounded-lg overflow-hidden bg-app-grey-5">
                        <AppVideoPlayer url={video.url || ''} />
                      </div>

                      {/* Feedback component */}
                      <div className="mt-6">
                        <VideoFeedback videoTitle={video.title || ''} videoUrl={video.url || ''} />
                      </div>
                    </div>

                    {/* Separator between videos (not after the last one) */}
                    {index < singleStepVideos.length - 1 && (
                      <div className="my-8 border-t border-app-grey-40" />
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* No videos at all */}
        {videos.length === 0 && previousMultiStepVideos.length === 0 && (
          <div className="ml-11">
            <NoVideoAvailable
              stepTitle={step.title || t('playboard.playForm.step', { number: step.stepNumber })}
            />
          </div>
        )}
      </div>
    )
  }

  const renderLegacyVideoSteps = (steps) => {
    return steps.map((step, index) => {
      const videos = (step.videos || []).slice(0, MAX_VIDEOS_PER_STEP) // Limit to max 2 videos

      return (
        <div key={index} className="mb-3 pb-3 border-b border-app-grey-40 last:border-b-0">
          {/* Step header with consistent styling */}
          <div className="mb-6">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-black text-white rounded-full flex items-center justify-center text-sm font-bold">
                {index + 1}
              </div>
              <div className="flex-1">
                {/* Step Title */}
                {step.title && (
                  <h3 className="text-base font-inter font-semibold text-app-black-100 mb-2">
                    {step.title}
                  </h3>
                )}

                {/* Step Description */}
                {step.description && (
                  <div className="text-sm text-app-grey-80 leading-relaxed mb-6">
                    <span className="[&_a]:text-app-blue-600 [&_a]:underline hover:[&_a]:text-app-blue-800 [&_a]:font-medium">
                      <Markdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          a: ({ node, ...props }) => (
                            // eslint-disable-next-line jsx-a11y/anchor-has-content
                            <a {...props} target="_blank" rel="noopener noreferrer" />
                          ),
                          p: ({ node, ...props }) => <p {...props} className="mb-2" />,
                          strong: ({ node, ...props }) => (
                            <strong {...props} className="font-bold" />
                          ),
                          ul: ({ node, ...props }) => (
                            <ul {...props} className="list-disc ml-4 pl-2 space-y-1 mt-2 mb-2" />
                          ),
                          li: ({ node, ...props }) => <li {...props} />,
                        }}
                      >
                        {step.description}
                      </Markdown>
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Videos for this step or No Video message */}
          {videos.length > 0 ? (
            <div className="ml-11">
              <h3 className="text-sm font-normal text-app-grey-50 font-inter mb-6">
                {t('playboard.playForm.videoOptions')}
              </h3>
              <div className="space-y-6">
                {videos.map((video, videoIndex) => {
                  if (!video) return null
                  return (
                    <div key={videoIndex}>
                      <div className="space-y-3">
                        {/* Video title */}
                        <h4 className="text-base font-semibold text-app-black-100 font-inter">
                          {video.title}
                        </h4>

                        {/* Video player */}
                        <div className="bg-white rounded-lg border border-app-grey-40 overflow-hidden">
                          <AppVideoPlayer url={video.url} />
                        </div>

                        {/* Feedback component with more top spacing */}
                        <div className="mt-6">
                          <VideoFeedback videoTitle={video.title} videoUrl={video.url} />
                        </div>
                      </div>

                      {/* Separator between videos (not after the last one) */}
                      {videoIndex < videos.length - 1 && (
                        <div className="my-8 border-t border-app-grey-40" />
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          ) : (
            <div className="ml-11">
              <NoVideoAvailable
                stepTitle={step.title || t('playboard.playForm.step', { number: index + 1 })}
              />
            </div>
          )}
        </div>
      )
    })
  }

  if (isLoading) {
    return <AppGenerateTutorialLoader />
  }

  if (tutorialNotFound) {
    return <img className="mt-4 rounded-2xl" src={AppTutorialError} alt="Not Found" />
  }

  if (!isViewMode || (!task?.howToDo && !generatedHowToDo)) {
    return null
  }

  return (
    <div className="mt-6">
      {/* Header with tutorial name and actions */}
      <div className="flex justify-between items-center mb-6 pb-4 border-b border-app-grey-20">
        <div className="flex items-center gap-1">
          <span className="text-sm font-inter font-normal text-app-grey-50">
            {t('playboard.playForm.tutorial')}
          </span>
          <span className="text-sm font-inter font-normal text-app-grey-50">/</span>
          <span className="text-sm font-inter font-semibold text-app-black-100">
            {activeTab === 'text'
              ? t('playboard.playForm.tutorialText')
              : t('playboard.playForm.tutorialVideo')}
          </span>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3">
          <AppButton
            label={
              activeTab === 'text'
                ? t('playboard.playForm.videoTutorial')
                : t('playboard.playForm.readTutorial')
            }
            variant="secondary"
            size="sm"
            onClick={() => {
              if (activeTab === 'text') {
                setActiveTab('video')
              } else {
                setActiveTab('text')
              }
            }}
          />
          <AppButton
            label={t('playboard.playForm.downloadPdf')}
            variant="primary"
            size="sm"
            onClick={() => {
              downloadPdf(
                null,
                `How-to-do-${(task?.name || '').replace(/ /g, '-')}.pdf`,
                `${task?.name || 'Task'} Tutorial`,
                task?.instructionForTask || '',
                cleanText(generatedHowToDo || task?.howToDo || '')
              )
            }}
          />
        </div>
      </div>

      {/* Content area */}
      <div id="tutorial-content" className="min-h-[200px]">
        {activeTab === 'text' ? (
          // Text Tutorial Tab
          <div className="prose prose-sm max-w-none">
            <div className="text-app-black-100 font-inter">
              <Markdown
                remarkPlugins={[remarkGfm]}
                components={{
                  // Links styling - make them stand out more
                  a: ({ node, ...props }) => (
                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                    <a
                      {...props}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-app-blue-600 underline hover:text-app-blue-800 transition-colors font-medium"
                    />
                  ),
                  // Main heading styling - smaller and more compact
                  h1: ({ node, ...props }) => (
                    <h1 {...props} className="text-lg font-semibold text-app-black-100 mb-4 mt-0">
                      {props.children}
                    </h1>
                  ),
                  // Section headings - more compact
                  h2: ({ node, ...props }) => (
                    <h2 {...props} className="text-base font-medium text-app-black-100 mb-3 mt-4">
                      {props.children}
                    </h2>
                  ),
                  h3: ({ node, ...props }) => (
                    <h3 {...props} className="text-base font-medium text-app-black-100 mb-3 mt-4">
                      {props.children}
                    </h3>
                  ),
                  // Paragraph styling
                  p: ({ node, ...props }) => (
                    <p {...props} className="mb-4 leading-relaxed text-sm text-app-grey-80" />
                  ),
                  // Ordered lists styling for numbered steps
                  ol: ({ node, ...props }) => (
                    <ol {...props} className="list-decimal pl-6 space-y-4 mb-6" />
                  ),
                  // Enhanced list items - simplified approach
                  li: ({ node, ...props }) => (
                    <li {...props} className="text-sm leading-relaxed text-app-black-100 pl-2 mb-2">
                      {props.children}
                    </li>
                  ),
                  // Unordered lists for sub-items with proper alignment
                  ul: ({ node, ...props }) => (
                    <ul
                      {...props}
                      className="list-disc ml-4 pl-4 space-y-1 mb-3 mt-2 text-sm text-app-grey-80"
                    />
                  ),
                  // Strong/bold text - make it more prominent
                  strong: ({ node, ...props }) => (
                    <strong {...props} className="font-bold text-app-black-100" />
                  ),
                  // Code inline styling
                  code: ({ node, ...props }) => (
                    <code
                      {...props}
                      className="bg-app-grey-10 px-2 py-1 rounded text-xs font-mono border"
                    />
                  ),
                  // Blockquotes for important notes
                  blockquote: ({ node, ...props }) => (
                    <blockquote
                      {...props}
                      className="border-l-4 border-app-blue-600 pl-4 py-3 bg-app-blue-50 text-sm mb-4 rounded-r-lg"
                    />
                  ),
                }}
              >
                {generatedHowToDo || task?.howToDo || ''}
              </Markdown>
            </div>
          </div>
        ) : (
          // Video Tutorial Tab
          <div className="space-y-6">
            {(() => {
              // Check if we have structured steps
              if (structuredSteps && structuredSteps.length > 0) {
                // Check if any step has videos
                const hasAnyVideos = structuredSteps.some(
                  (step) =>
                    step.videos &&
                    step.videos.length > 0 &&
                    step.videos.some((video) => video !== null)
                )

                if (!hasAnyVideos) {
                  return <NoVideoTutorialsEmptyState />
                }

                return (
                  <div>{structuredSteps.map((step) => renderVideoStep(step, structuredSteps))}</div>
                )
              }

              // Legacy format fallback
              return (
                <Controller
                  control={control}
                  name="howToLinks"
                  render={({ field }) => {
                    if (!field?.value || field?.value.length === 0 || !field?.value[0].steps) {
                      return <NoVideoTutorialsEmptyState />
                    }

                    const steps = JSON.parse(field?.value[0].steps)

                    // Check if any step has videos in legacy format
                    const hasAnyVideos = steps.some((step) => step.videos && step.videos.length > 0)

                    if (!hasAnyVideos) {
                      return <NoVideoTutorialsEmptyState />
                    }

                    return <div>{renderLegacyVideoSteps(steps)}</div>
                  }}
                />
              )
            })()}
          </div>
        )}
      </div>
    </div>
  )
}
